import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { shopApi } from "@/lib/shopApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import AuthShell, { fieldCls, primaryBtn } from "@/components/store/AuthShell";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await shopApi.post("/member/auth/forgot-password", { email }); setSent(true); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Reset your password" subtitle="Enter your account email and we'll send you a reset link." testid="forgot-password-page">
      {sent ? (
        <p className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm leading-relaxed text-slate-700" data-testid="forgot-sent">
          If an account exists for <strong>{email}</strong>, a reset link is on its way. It expires in 1 hour.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="f-email">Email address</Label><Input id="f-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={fieldCls} data-testid="forgot-email-input" /></div>
          {err && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="forgot-error">{err}</p>}
          <Button type="submit" disabled={busy} className={primaryBtn} data-testid="forgot-submit-button">{busy ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
      <Link to="/login" className="mt-6 block text-center text-sm font-semibold text-sky-700 hover:text-sky-900" data-testid="forgot-back-login">Back to log in</Link>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw !== confirm) return setErr("Passwords do not match.");
    setBusy(true);
    try { await shopApi.post("/member/auth/reset-password", { token: params.get("token") || "", password: pw }); nav("/login?reset=1", { replace: true }); }
    catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Choose a new password" testid="reset-password-page">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5"><Label htmlFor="n-pw">New password</Label><Input id="n-pw" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} className={fieldCls} autoComplete="new-password" placeholder="At least 8 characters" data-testid="reset-password-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="n-pw2">Confirm new password</Label><Input id="n-pw2" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={fieldCls} autoComplete="new-password" data-testid="reset-confirm-input" /></div>
        {err && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="reset-error">{err}</p>}
        <Button type="submit" disabled={busy} className={primaryBtn} data-testid="reset-submit-button">{busy ? "Saving…" : "Update password"}</Button>
      </form>
    </AuthShell>
  );
}
